import { Account } from "@/entities/auth/Account";
import { Module } from "vuex";
import { RootStoreState } from "@/store/RootStoreState";
import { UserService } from "@/services/userService";
import { LoginModel } from "@/entities/LoginModel";
import { SignupModel } from "@/entities/SignupModel";
import router from "@/router";
import { Component, Vue } from "vue-property-decorator";
import { Role } from "@/entities/auth/Role";
import { LoginWithKeyModel } from "@/entities/LoginWithKeyModel";
import { toastService } from "@/services/toastService";

interface AuthenticationState {
  account: Account | null;
  loading: boolean;
  isLoggingIn: boolean;
}

const initialStateAccount = UserService.getAccount();
const authenticationModule: Module<AuthenticationState, RootStoreState> = {
  namespaced: true,
  state: {
    account: initialStateAccount,
    loading: initialStateAccount === null,
    isLoggingIn: false,
  },
  getters: {
    isAuthenticated(state): boolean {
      return state.account !== null;
    },
    account(state): Account | null {
      return state.account;
    },
    isExpert(state): boolean {
      return state.account !== null && state.account.role === Role.Expert;
    },
    isAdmin(state): boolean {
      return state.account !== null && state.account.role === Role.Admin;
    },
    isAuthLoading(state): boolean {
      return state.loading;
    },
    isLoggingIn(state): boolean {
      return state.isLoggingIn;
    },
    // Helper getter to check if terms modal should be shown
    shouldShowTermsModal(state): boolean {
      if (!state.account) return false;
      
      // Check if terms have been accepted in localStorage
      const termsAccepted = localStorage.getItem('proxymed_terms_accepted');
      return termsAccepted !== 'true';
    }
  },
  mutations: {
    login(state: AuthenticationState, account: Account) {
      state.account = account;
      state.loading = false;
    },
    logout(state: AuthenticationState) {
      state.account = null;
      state.loading = false;
    },
    finishLoading(state: AuthenticationState) {
      state.loading = false;
    },
    SET_LOGGING_IN(state: AuthenticationState, value: boolean) {
      state.isLoggingIn = value;
    },
    SET_AUTH_LOADING(state: AuthenticationState, value: boolean) {
      state.loading = value;
    }
  },
  
  actions: {

    async login({ commit, getters }, loginModel: LoginModel) {
      try {
        commit("SET_LOGGING_IN", true);
        
        const account = await UserService.login(
          loginModel.email,
          loginModel.password,
          loginModel.rememberMe
        );
        commit("login", account);
        commit("error", null, { root: true });

        // Check if user has accepted terms
        const termsAccepted = localStorage.getItem('proxymed_terms_accepted');
        
        if (termsAccepted === 'true') {
          // Terms already accepted, proceed with normal login flow
          toastService.showSuccess('Logged in successfully!');

          // Small delay to let notification show
          await new Promise(resolve => setTimeout(resolve, 800));

          // Navigate based on user role
          if (loginModel.returnUrl) {
            await router.push(loginModel.returnUrl as string);
          } else if (account.role === Role.Expert) {
            await router.push("/tools/edit");
          } else if (account.role === Role.Admin) {
            await router.push("/admin/AdminPanel");
          } else {
            await router.push("/");
          }
        } else {
          // Terms not accepted - don't redirect yet
          // The Login component will show the terms modal
          console.log("User needs to accept terms before proceeding");
          // Don't show the normal success toast yet - let the modal handle it
        }
        
      } catch (error) {
        commit("error", error, { root: true });
        console.log(error);
        throw error;
      } finally {
        commit("SET_LOGGING_IN", false);
      }
    },

    // New action to handle post-terms acceptance navigation
    async navigateAfterTermsAccepted({ getters }, { returnUrl = null } = {}) {
      const account = getters.account;
      if (!account) {
        console.error("No account found for navigation");
        return;
      }

      try {
        // Small delay to let notification show
        await new Promise(resolve => setTimeout(resolve, 800));

        // Navigate based on user role and return URL
        if (returnUrl) {
          await router.push(returnUrl);
        } else if (account.role === Role.Expert) {
          await router.push("/tools/edit");
        } else if (account.role === Role.Admin) {
          await router.push("/admin/AdminPanel");
        } else {
          await router.push("/");
        }
      } catch (error) {
        console.error("Navigation after terms acceptance failed:", error);
      }
    },

    // Fixed signup action
    async signup({ commit }, signupModel: SignupModel) {
      try {
        // Call UserService to handle the registration
        const result = await UserService.signup(
          signupModel.firstName,
          signupModel.lastName,
          signupModel.email,
          signupModel.phone,
          signupModel.companyName,
          signupModel.referrerSource,
          signupModel.password
        );
        
        // Clear any previous errors
        commit("error", null, { root: true });
        
        // Don't automatically log in after signup - let them verify email first
        // Show success message via toast service
        toastService.showSuccess('Account created successfully! Please check your email to verify your account.');
        
        // Small delay to let notification show
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Return the result (could contain verification info, user ID, etc.)
        return result;
        
      } catch (error) {
        commit("error", error, { root: true });
        console.log(error);
        throw error;
      }
    },

    async loginWithKey({ commit }, loginModel: LoginWithKeyModel) {
      try {
        const account = await UserService.loginWithKey(
          loginModel.privateKey,
          loginModel.rememberMe
        );
        commit("login", account);
        commit("error", null, { root: true });

        // Check if user has accepted terms for key-based login too
        const termsAccepted = localStorage.getItem('proxymed_terms_accepted');
        
        if (termsAccepted === 'true') {
          if (loginModel.returnUrl) {
            await router.push(loginModel.returnUrl as string);
          } else {
            await router.push("/");
          }
        }
        // If terms not accepted, let the component handle showing the modal
        
      } catch (error) {
        commit("error", error, { root: true });
        console.log(error);
        throw error;
      }
    },

    async logout({ commit }) {
      UserService.logout();
      
      // Clear terms acceptance when logging out
      localStorage.removeItem('proxymed_terms_accepted');
      localStorage.removeItem('proxymed_terms_accepted_at');
      
      commit("logout");
      await router.push("/login");
    },

    async resendVerification({ commit }, { accountId }) {
      try {
        const result = await UserService.resendVerification(accountId);
        commit("error", null, { root: true });
        return result;
      } catch (error) {
        commit("error", error, { root: true });
        throw error;
      }
    }
  },
};

export default authenticationModule;